import { Switch } from "@/components/ui/switch";
import { useLabels } from "@/hooks/use-labels";
import {
  useUpdateUserSettings,
  useUserSettings,
} from "@/hooks/use-user-settings";
import {
  getPushSubscription,
  isPushSupported,
  requestNotificationPermission,
  subscribePush,
  unsubscribePush,
} from "@/lib/push";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Bell,
  Check,
  ChevronRight,
  Loader2,
  Store,
  Tag,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/settings/")({
  component: SettingsPage,
});

function SettingsPage() {
  const navigate = useNavigate();
  const { data: labels, isLoading: labelsLoading } = useLabels();
  const { data: settings, isLoading: settingsLoading } = useUserSettings();
  const updateSettings = useUpdateUserSettings();

  const currentDefaultLabelId = settings?.defaultLabelId ?? null;
  const isLoading = labelsLoading || settingsLoading;

  const [notifyEnabled, setNotifyEnabled] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(true);
  const [permission, setPermission] =
    useState<NotificationPermission>("default");
  const pushSupported = isPushSupported();

  useEffect(() => {
    if (!pushSupported) {
      setNotifyLoading(false);
      return;
    }
    setPermission(Notification.permission);
    getPushSubscription()
      .then((sub) => setNotifyEnabled(!!sub))
      .finally(() => setNotifyLoading(false));
  }, [pushSupported]);

  const handleNotifyToggle = async (checked: boolean) => {
    setNotifyLoading(true);
    try {
      if (checked) {
        const perm = await requestNotificationPermission();
        setPermission(perm);
        if (perm !== "granted") {
          toast.error("ブラウザの通知が許可されていません", {
            description: "ブラウザの設定から通知を許可してください",
          });
          return;
        }
        const sub = await subscribePush();
        setNotifyEnabled(!!sub);
        if (sub) toast.success("Push通知を有効にしました");
      } else {
        await unsubscribePush();
        setNotifyEnabled(false);
        toast.success("Push通知を無効にしました");
      }
    } catch {
      toast.error("設定の変更に失敗しました");
    } finally {
      setNotifyLoading(false);
    }
  };

  const handleSelect = (labelId: string) => {
    if (labelId === currentDefaultLabelId) return;
    updateSettings.mutate({ defaultLabelId: labelId });
  };

  return (
    <div className="flex flex-1 flex-col">
      {/* ヘッダー */}
      <header className="flex items-center justify-between border-b px-4 py-3">
        <button
          type="button"
          onClick={() => navigate({ to: "/" })}
          className="rounded-full p-1 text-muted-foreground hover:bg-muted"
          aria-label="戻る"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-bold text-foreground">設定</h1>
        <div className="w-7" />
      </header>

      {/* Push通知 */}
      {pushSupported && (
        <div className="border-b px-4 py-4">
          <div className="flex items-center gap-3">
            <Bell className="h-4 w-4 text-muted-foreground" />
            <span className="flex-1 text-sm text-foreground">Push通知</span>
            {notifyLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : (
              <Switch
                checked={notifyEnabled}
                onCheckedChange={handleNotifyToggle}
                aria-label="Push通知の有効・無効"
              />
            )}
          </div>
          <p className="mt-1 pl-7 text-xs text-muted-foreground">
            {notifyLoading
              ? "確認中..."
              : notifyEnabled
                ? "有効 — 他のメンバーの操作や同期時に通知が届きます"
                : permission === "denied"
                  ? "⚠ ブラウザで通知がブロックされています。設定から許可してください"
                  : "無効 — スイッチをオンにすると通知を受け取れます"}
          </p>
        </div>
      )}

      {/* お店の管理 リンク */}
      <div className="border-b px-4 py-4">
        <Link
          to="/settings/venues"
          className="flex items-center gap-3 text-sm text-foreground"
        >
          <Store className="h-4 w-4 text-muted-foreground" />
          <span className="flex-1">お店の管理</span>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </Link>
      </div>

      {/* デフォルトラベル セクション */}
      <div className="flex-1 px-4 py-4">
        <h2 className="text-sm font-semibold text-foreground">
          デフォルトラベル
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          アプリ起動時に表示するラベル
        </p>

        {isLoading ? (
          <p className="py-8 text-center text-muted-foreground">
            読み込み中...
          </p>
        ) : !labels || labels.length === 0 ? (
          <p className="py-8 text-center text-muted-foreground">
            ラベルを作成してください
          </p>
        ) : (
          <ul className="mt-3">
            {labels.map((label) => {
              const isSelected = label.id === currentDefaultLabelId;
              return (
                <li key={label.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(label.id)}
                    className="flex w-full items-center gap-3 border-b py-3 text-left"
                    aria-pressed={isSelected}
                  >
                    <Tag className="h-4 w-4 text-muted-foreground" />
                    <span className="flex-1 text-sm text-foreground">
                      {label.name}
                    </span>
                    {isSelected && <Check className="h-4 w-4 text-primary" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
